<script setup lang="ts">
import { defineEmits, withDefaults, ref, computed, watch } from 'vue'
import Button from 'primevue/button'
import FloorplanItem from './FloorplanItem.vue'

import { Floorplan } from '@/entities/Floorplan'

import Select from 'primevue/select'

import FloatLabel from 'primevue/floatlabel'

const props = withDefaults(
  defineProps<{
    floorplans: Floorplan[]
    activeFloorplan?: Floorplan
    loading?: boolean
    showCreate?: boolean
    showActions?: boolean
  }>(),
  {
    loading: false,
    showCreate: false,
    showActions: false,
  },
)

const emit = defineEmits(['floorplanSelected', 'createClicked', 'editFloorplan', 'deleteFloorplan'])

const selectFloorplan = (floorplan) => {
  emit('floorplanSelected', floorplan)
}

const handleCreateClick = () => {
  emit('createClicked')
}

const selectedFloorplan = ref(props.activeFloorplan || null)

const floorplans = computed(() => props.floorplans)
watch(
  () => props.activeFloorplan,
  (newVal) => {
    selectedFloorplan.value = newVal
  },
  { deep: true, immediate: true },
)
watch(
  () => props.floorplans.length,
  (newLength) => {
    if (newLength === 0) {
      selectedFloorplan.value = null
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="select-container">
    <FloatLabel class="w-full md:w-56">
      <Select
        inputId="flooplan-select"
        class="select"
        v-model="selectedFloorplan"
        :options="floorplans"
        optionLabel="name"
        dataKey="uuid"
        :loading="loading"
        emptyMessage="No floorplans yet"
      >
        <template #option="slotProps">
          <FloorplanItem
            :key="slotProps.option.uuid"
            :floorplan="slotProps.option"
            :is-active="slotProps.option.uuid === activeFloorplan?.uuid"
            :show-actions="showActions"
            @floorplan-selected="selectFloorplan(slotProps.option)"
            @delete-floorplan="(floorplan) => emit('deleteFloorplan', floorplan)"
            @edit-floorplan="(floorplan) => emit('editFloorplan', floorplan)"
          />
        </template>
        <template v-if="showCreate" #footer>
          <div class="footer">
            <Button class="add-floorplan-btn" severity="secondary" text size="small" @click="handleCreateClick">
              Add New
              <span>
                <i class="pi pi-plus" />
              </span>
            </Button>
          </div>
        </template>
      </Select>
      <label for="floorplan-select">Select Floorplan</label>
    </FloatLabel>
  </div>
</template>

<style scoped>
.floorplans {
  box-shadow: var(--card-shadow);
}

.floorplans :deep(.p-panel-content) {
  border-top: 1px solid var(--p-surface-300);
  padding: 0;
}

.floorplans ul.floorplans-list {
  margin: 0;
  padding: 0;
  max-height: 100px;
  overflow-y: auto;
  list-style: none;
  border-radius: 0 0 var(--border-radius) var(--border-radius);
}
div.empty-floorplan-list {
  padding: 0 16px 8px 16px;
  font-size: 12px;
}
div.select-container {
  width: 100%;
  padding-top: 20px;
}
.select {
  width: 100%;
}
.footer {
  padding: 0 0.25rem 0.25rem 0.25rem;
}
.add-floorplan-btn {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
}
</style>
